import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { api } from '@/lib/api'
import { fmtDate } from '@/lib/utils'
import { PageHeader } from '@/components/layout/PageHeader'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { QueryError } from '@/components/ui/QueryError'
import { Button } from '@/components/ui/Button'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
  EmptyState,
  LoadingRows,
} from '@/components/ui/DataTable'

interface AdminZone {
  id: number
  name: string
  createdAt?: string
  ownerId?: number
  ownerUsername?: string
  ownerName?: string
  memberCount: number
  channelCount: number
}

export function CommunitiesPage() {
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-zones', search],
    queryFn: () => api<{ zones: AdminZone[] }>(`/api/admin/zones?q=${encodeURIComponent(search)}&limit=100`),
  })

  const zones = data?.zones || []

  return (
    <div>
      <PageHeader
        title="Communities"
        description="All platform zones (communities). Ownership transfers and per-zone moderation are managed in chat zone settings."
      />

      <form
        className="flex gap-3 mb-6 max-w-md"
        onSubmit={(e) => {
          e.preventDefault()
          setSearch(q.trim())
        }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#93a5ad]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by zone or owner…"
            className="pl-10"
          />
        </div>
        <Button type="submit" variant="secondary" className="shrink-0">Search</Button>
      </form>

      {error ? (
        <QueryError title="Couldn’t load communities" error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable>
          <table className="w-full min-w-[640px]">
            <DataTableHead>
              <DataTableHeaderCell>Zone</DataTableHeaderCell>
              <DataTableHeaderCell>Owner</DataTableHeaderCell>
              <DataTableHeaderCell>Members</DataTableHeaderCell>
              <DataTableHeaderCell>Channels</DataTableHeaderCell>
              <DataTableHeaderCell>Created</DataTableHeaderCell>
            </DataTableHead>
            {isLoading ? (
              <LoadingRows cols={5} />
            ) : zones.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={5}>
                    <EmptyState title="No communities found" description="Try a different search term." />
                  </td>
                </tr>
              </tbody>
            ) : (
              <DataTableBody>
                {zones.map((z) => (
                  <DataTableRow key={z.id}>
                    <DataTableCell>
                      <div className="font-semibold">{z.name}</div>
                      <div className="text-xs text-[#93a5ad] mt-0.5">#{z.id}</div>
                    </DataTableCell>
                    <DataTableCell>
                      {z.ownerUsername ? (
                        <span>
                          @{z.ownerUsername}
                          {z.ownerName && (
                            <span className="text-[#93a5ad] text-xs block">{z.ownerName}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[#93a5ad]">—</span>
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant="muted">{z.memberCount}</Badge>
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant="muted">{z.channelCount}</Badge>
                    </DataTableCell>
                    <DataTableCell className="text-[#9aabbb] text-xs">
                      {fmtDate(z.createdAt)}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            )}
          </table>
        </DataTable>
      )}
    </div>
  )
}
